import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';

import { Uris } from '../Uris';
import { GlobalFunction } from '../globalFunction';
import {
  UsersForm,
  SubmitUserList,
  UsersListItem,
  UserKind,
  permissions,
} from '../../interfaces/users/users.interfaces';
import { User } from '../../interfaces/auth/auth.interfaces';

const FULL_PERMISSIONS: permissions = {
  stores: true,
  sectors: true,
  routes: true,
  logistics: true,
  sales: true,
  users: true,
  settings: true,
  control: true,
  inventory: true,
  courier_access: true,
  suggested_calculator: true,
  suggested_configuration: true,
  routes_assignation: true,
  stock: true,
  inventory_indicators: true,
  quality_surveys: true,
  no_sales_module: true,
};

const SUPERVISOR_PERMISSIONS: permissions = {
  stores: false,
  sectors: false,
  routes: false,
  logistics: false,
  sales: false,
  users: false,
  settings: false,
  control: true,
  inventory: false,
  courier_access: false,
  suggested_calculator: false,
  suggested_configuration: false,
  routes_assignation: false,
  stock: false,
  inventory_indicators: false,
  quality_surveys: false,
  no_sales_module: false,
};

const USER_URIS: Record<UserKind, string> = {
  admin: Uris.API_ADMINS,
  courier: Uris.API_COURIERS,
  warehouse: Uris.API_WAREHOUSE_STAFF,
  sales: Uris.API_SALES_STAFF,
  supervisor: Uris.API_SUPERVISORS,
};

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  constructor(
    private http: HttpClient,
    public router: Router,
    public globalFunction: GlobalFunction,
  ) {}
  permissions: permissions = { ...FULL_PERMISSIONS };
  isSupervisor = false;
  /* null = sin restricción de rutas */
  assignedRouteIds: number[] | null = null;

  /**
   * Restablece el acceso por defecto para no heredar el de la sesión anterior.
   */
  resetAccess(): void {
    this.permissions = { ...FULL_PERMISSIONS };
    this.isSupervisor = false;
    this.assignedRouteIds = null;
  }

  /**
   * Limita la sesión actual al mapa operativo y a las rutas indicadas.
   *
   * @param routeIds Rutas asignadas al supervisor.
   * @returns Los permisos aplicados.
   */
  applySupervisorAccess(routeIds: number[]): permissions {
    this.permissions = { ...SUPERVISOR_PERMISSIONS };
    this.isSupervisor = true;
    this.assignedRouteIds = routeIds;
    return this.permissions;
  }

  /**
   * Indica si la sesión actual puede ver una ruta.
   *
   * @param routeId Identificador de la ruta.
   * @returns `true` si no hay restricción o la ruta está asignada.
   */
  isRouteAllowed(routeId: number | undefined): boolean {
    if (this.assignedRouteIds === null) {
      return true;
    }
    return routeId !== undefined && this.assignedRouteIds.includes(routeId);
  }

  /* OBTENER TODOS LOS USUARIOS */
  getUsers(query: string): Observable<SubmitUserList> {
    return this.http
      .get<SubmitUserList>(`${Uris.API_USERS}${query}`, this.globalFunction.headers())
      .pipe(map((resp: SubmitUserList) => resp));
  }

  /**
   * Obtiene los usuarios de un tipo.
   *
   * @param kind Tipo de usuario.
   * @param query Query string de filtros y paginación.
   * @returns Lista de usuarios, paginada o no según la respuesta del backend.
   */
  getList(kind: UserKind, query: string): Observable<UsersListItem[] | SubmitUserList> {
    return this.http
      .get<UsersListItem[] | SubmitUserList>(
        `${USER_URIS[kind]}${query}`,
        this.globalFunction.headers(),
      )
      .pipe(map((resp: UsersListItem[] | SubmitUserList) => resp));
  }

  /**
   * Obtiene el perfil del usuario autenticado.
   *
   * @returns El usuario de la sesión actual.
   */
  getProfile(): Observable<User> {
    return this.http
      .get<User>(`${Uris.API_USERS}profile/`, this.globalFunction.headers())
      .pipe(map((resp: User) => resp));
  }

  /* OBTENER TODOS LOS REPARTIDORES */
  getCouriers(query: string): Observable<UsersListItem[] | SubmitUserList> {
    return this.http
      .get<UsersListItem[] | SubmitUserList>(
        `${Uris.API_COURIERS}${query}`,
        this.globalFunction.headers(),
      )
      .pipe(map((resp: UsersListItem[] | SubmitUserList) => resp));
  }

  /* CREAR UN NUEVO USUARIO */
  postUser(query: string, type: UserKind, data: FormData | UsersForm): Observable<UsersForm> {
    const headers =
      data instanceof FormData
        ? this.globalFunction.headersMultipart()
        : this.globalFunction.headers();

    return this.http
      .post<UsersForm>(`${USER_URIS[type]}${query}`, data, headers)
      .pipe(map((resp: UsersForm) => resp));
  }

  update(id: number, type: UserKind, data: FormData | Partial<UsersForm>): Observable<UsersForm> {
    const headers =
      data instanceof FormData
        ? this.globalFunction.headersMultipart()
        : this.globalFunction.headers();

    return this.http
      .patch<UsersForm>(`${USER_URIS[type]}${id}/`, data, headers)
      .pipe(map((resp: UsersForm) => resp));
  }

  patch(id: number, type: UserKind, data: Record<string, unknown>): Observable<UsersForm> {
    return this.http
      .patch<UsersForm>(`${USER_URIS[type]}${id}/`, data, this.globalFunction.headers())
      .pipe(map((resp: UsersForm) => resp));
  }
  activeDisabledUser(id: number, active: boolean, type: UserKind): Observable<{ detail: string }> {
    return this.http
      .post<{
        detail: string;
      }>(
        `${USER_URIS[type]}${id}/${active ? 'enable' : 'disable'}/`,
        {},
        this.globalFunction.headers(),
      )
      .pipe(map((resp: { detail: string }) => resp));
  }

  delete(id: number, type: UserKind): Observable<UsersForm> {
    return this.http
      .delete<UsersForm>(`${USER_URIS[type]}${id}/`, this.globalFunction.headers())
      .pipe(map((resp: UsersForm) => resp));
  }
  getUserById(id: number): Observable<UsersForm> {
    return this.http
      .get<UsersForm>(`${Uris['API_USERS']}${id}/`, this.globalFunction.headers())
      .pipe(map((resp: UsersForm) => resp));
  }
  getUserPermissionsById(id: number): Observable<permissions> {
    return this.http
      .get<permissions>(`${Uris.API_ADMINS}${id}/permissions/`, this.globalFunction.headers())
      .pipe(map((resp: permissions) => resp));
  }
  updateAdminPermissionsById(id: number, data: Record<string, unknown>): Observable<permissions> {
    return this.http
      .put<permissions>(
        `${Uris.API_ADMINS}${id}/change_permissions/`,
        data,
        this.globalFunction.headers(),
      )
      .pipe(map((resp: permissions) => resp));
  }

  changePasswordById(id: number, data: Record<string, unknown>): Observable<permissions> {
    return this.http
      .put<permissions>(
        `${Uris.API_USERS}${id}/change_password/`,
        data,
        this.globalFunction.headers(),
      )
      .pipe(map((resp: permissions) => resp));
  }
  setPasswordById(id: number, data: Record<string, unknown>): Observable<permissions> {
    return this.http
      .post<permissions>(
        `${Uris.API_USERS}${id}/set_password/`,
        data,
        this.globalFunction.headers(),
      )
      .pipe(map((resp: permissions) => resp));
  }
}
